import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ISubtreeRollup } from '@stem/contracts';
import { apiClient } from '../services/api';
import { EmptyState, Meter, Section, Stat } from './parts';

/** A head teacher or academic coordinator: staff, uptake, certificates. */
export function SchoolAdminDashboard() {
  const { orgSlug = '' } = useParams();

  const rollup = useQuery({
    queryKey: ['rollup'],
    queryFn: () => apiClient.get<ISubtreeRollup>('/v1/analytics/rollup'),
  });

  const subscription = useQuery({
    queryKey: ['subscription'],
    queryFn: () =>
      apiClient.get<{ planCode: string; seats: number; seatsUsed: number; status: string } | null>(
        '/v1/billing/subscription',
      ),
  });

  const totals = rollup.data?.totals;

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink-900">School dashboard</h1>
      <p className="text-sm text-ink-600">
        Teacher professional development, uptake and certification across your school.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Members" value={totals?.members ?? '—'} />
        <Stat label="Active learners" value={totals?.activeLearners ?? '—'} />
        <Stat
          label="Completion rate"
          value={totals ? `${totals.completionRate}%` : '—'}
          tone={(totals?.completionRate ?? 0) >= 60 ? 'good' : 'warn'}
          hint={totals ? `${totals.completions} of ${totals.enrollments} enrolments` : undefined}
        />
        <Stat label="Certificates" value={totals?.certificatesIssued ?? '—'} />
      </div>

      {subscription.data && (
        <Section title="Subscription" action={{ label: 'Manage', to: `/o/${orgSlug}/billing` }}>
          <div className="card flex flex-wrap items-center gap-4 text-sm">
            <span className="font-semibold capitalize text-ink-900">
              {subscription.data.planCode}
            </span>
            <span className="text-ink-600">
              {subscription.data.seatsUsed} of{' '}
              {subscription.data.seats === 0 ? 'unlimited' : subscription.data.seats} seats assigned
            </span>
            <span
              className={
                subscription.data.status === 'active'
                  ? 'badge bg-emerald-100 text-emerald-800'
                  : 'badge bg-savanna-400/20 text-savanna-600'
              }
            >
              {subscription.data.status}
            </span>
            {/*
              A school on an unmetered plan does not have to assign anything —
              `seatLimit: null` seats everyone. Saying so here stops a head
              teacher hunting for a seat-assignment screen that has no purpose.
            */}
            {subscription.data.seats === 0 && (
              <span className="text-xs text-ink-500">
                Every member of this school is covered automatically.
              </span>
            )}
          </div>
        </Section>
      )}

      <Section title="Laboratory uptake">
        {totals && totals.labRuns > 0 ? (
          <div className="card flex items-center gap-3 text-sm">
            <span className="text-2xl font-bold tabular-nums text-ink-900">{totals.labRuns}</span>
            <span className="text-ink-600">laboratory sessions started by your staff and learners</span>
          </div>
        ) : (
          <EmptyState>
            No laboratory sessions yet.{' '}
            <Link to={`/o/${orgSlug}/labs`} className="text-brand-700 underline">
              Open one yourself
            </Link>{' '}
            to see what your teachers will see.
          </EmptyState>
        )}
      </Section>

      <Section title="People" action={{ label: 'Manage people', to: `/o/${orgSlug}/members` }}>
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-ink-100 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-4 py-2">Organisation</th>
                <th className="px-4 py-2">Members</th>
                <th className="px-4 py-2">Enrolments</th>
                <th className="px-4 py-2">Completion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {(rollup.data?.byOrg ?? []).map(row => (
                <tr key={row.orgId}>
                  <td className="px-4 py-2">{row.orgName}</td>
                  <td className="px-4 py-2 tabular-nums">{row.members}</td>
                  <td className="px-4 py-2 tabular-nums">{row.enrollments}</td>
                  <td className="px-4 py-2">
                    <Meter value={row.completionRate} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
